import type { Edge } from '../../../core/models/scene';

/**
 * Color palette used by the Secondary Reports lens to render each employee's
 * dotted/matrix reporting line in a distinct color. The hash is keyed on the
 * employee node id (the `from` endpoint of the secondary edge) so a given
 * person always gets the same color across the diagram.
 */
export const SECONDARY_REPORTS_LENS_PALETTE = [
  '#E11D48',
  '#2563EB',
  '#16A34A',
  '#EA580C',
  '#7C3AED',
  '#0F766E',
  '#C026D3',
  '#B45309',
] as const;

/**
 * Default secondary connector accent. Mirrors the fallback used by
 * `EdgeConnector.getStrokeColor` so collapsed dot highlights match the line
 * users would see if the branch were expanded.
 */
export const SECONDARY_REPORTS_DEFAULT_COLOR = '#0EA5A4';

/**
 * Fixed color for collapsed-dot-grid selection highlights: hidden people in a
 * collapsed branch who secondary-report to a currently selected node. Intentionally
 * does not mirror edge stroke color (default dotted lines are slate gray and
 * blend into sibling dots). Amber-500 reads clearly on the light gray grid and
 * sits outside the Secondary Reports lens palette hues.
 */
export const SECONDARY_REPORTS_SELECTION_DOT_COLOR = '#F59E0B';

export function resolveSecondarySelectionDotColor(): string {
  return SECONDARY_REPORTS_SELECTION_DOT_COLOR;
}

export const hashSecondaryReportsKey = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

export interface ResolveSecondaryReportsColorInput {
  /** Employee node id (the `from` endpoint of the secondary edge). */
  fromNodeId: string;
  /** Optional underlying edge so a user-set `attrs.color` wins over palette/fallback. */
  edge?: Edge | null;
  /** True when the Secondary Reports lens is enabled (multi-color mode). */
  secondaryLensEnabled: boolean;
}

/**
 * Resolve the stroke color the canvas would use for a secondary reporting line
 * originating at `fromNodeId`. Used by `EdgesRenderer` for visible dotted /
 * matrix line strokes.
 *
 * Precedence:
 *   1. Lens palette colored by `hash(fromNodeId)` when the lens is enabled.
 *      This intentionally overrides per-edge color so the lens "view all"
 *      recoloring behaves consistently across the diagram.
 *   2. `edge.attrs.color` if explicitly set.
 *   3. `SECONDARY_REPORTS_DEFAULT_COLOR`.
 *
 * Collapsed-dot selection highlights use `resolveSecondarySelectionDotColor`
 * instead so they stay legible regardless of default edge gray/teal.
 */
export function resolveSecondaryReportsColor(
  input: ResolveSecondaryReportsColorInput,
): string {
  if (input.secondaryLensEnabled) {
    const idx = hashSecondaryReportsKey(input.fromNodeId) % SECONDARY_REPORTS_LENS_PALETTE.length;
    return SECONDARY_REPORTS_LENS_PALETTE[idx];
  }
  const attrs = (input.edge?.attrs ?? {}) as Record<string, unknown>;
  const attrColor = attrs.color;
  if (typeof attrColor === 'string' && attrColor.length > 0) return attrColor;
  return SECONDARY_REPORTS_DEFAULT_COLOR;
}
